<template>
  <a-modal
    title="Join Alert"
    v-if="visible"
    :visible="visible"
    :destroy-on-close="true"
    @cancel="closeModal"
    width="860px"
  >
    <a-modal
      v-model="visibleCloneModal"
      title="Create New Profile"
      width="1000px"
      :footer="null"
      :destroy-on-close="true"
    >
      <clone-associate :associate-number="alert.data.associate_number" @close="endCloneAssociate" />
    </a-modal>

    <invite-user :associate="associate" :visible.sync="visibleInviteModal" @add="afterSendLetter" />

    <a-modal
      :visible="visibleConfirmModal"
      @cancel="closeConfirmModal"
      title="Create New Profile"
      width="600px"
      :destroy-on-close="true"
    >
      <a-input v-model="reason" placeholder="Enter Reason" autocomplete="off" />
      <template slot="footer">
        <div class="flex justify-content-between">
          <a-button @click="closeConfirmModal"> Cancel </a-button>
          <a-button icon="close" type="danger" :loading="loadingReject" :disabled="!reason" @click="reject">
            Confirm Reject
          </a-button>
        </div>
      </template></a-modal
    >

    <div v-if="!associate || loading" class="loading-spin" style="height: 650px">
      <a-spin />
    </div>
    <div v-else class="grid">
      <profile-edit
        :visible.sync="visibleEditModal"
        @clone="visibleCloneModal = true"
        @edit="endEditAssociate"
        @saved="handleProfileSaved"
      />

      <div class="col-12">
        <a-alert
          message="A new user wants to join your company. Review their application and accept or reject it"
          type="info"
          show-icon
        />
      </div>

      <div class="col flex flex-column gap-2">
        <h3>Personal</h3>
        <a-descriptions :column="1" size="small" class="col-fixed p-0" :colon="false">
          <a-descriptions-item label="Photo">
            <user-avatar :width="64" :height="64" :image-path="associate.photo_url ? associate.photo_url : undefined" />
          </a-descriptions-item>
          <a-descriptions-item label="First Name">
            {{ associate.first_name }}
          </a-descriptions-item>
          <a-descriptions-item label="Last Name">
            {{ associate.last_name }}
          </a-descriptions-item>
          <a-descriptions-item label="Email">
            {{ associate.email }}
          </a-descriptions-item>
          <a-descriptions-item label="Phone">
            {{ associate.phone }}
          </a-descriptions-item>
          <a-descriptions-item label="Associate Number">
            {{ associate.associate_number }}
          </a-descriptions-item>
          <a-descriptions-item :label="getTaxIdentifierLabel(associate.is_ssn)">
            {{ associate.social_security_number ?? ' - ' }}
          </a-descriptions-item>
          <a-descriptions-item label="Birth Date">
            {{ associate.birth_date ? formatDateString(associate.birth_date) : ' - ' }}
          </a-descriptions-item>
          <a-descriptions-item label="Address">
            {{ `${associate.address?.address_line1 ?? ' - '}` }}
            {{ associate.address?.address_line2 }}
          </a-descriptions-item>
          <a-descriptions-item label="City">
            {{ associate.address?.city ?? ' - ' }}
          </a-descriptions-item>
          <a-descriptions-item label="State">
            {{ findStateByCode(associate.address?.state) ?? ' - ' }}
          </a-descriptions-item>
          <a-descriptions-item label="Country">
            {{ findCountryByCode(associate.address?.country) ?? ' - ' }}
          </a-descriptions-item>
          <a-descriptions-item label="Zip">
            {{ associate.address?.zipcode ?? ' - ' }}
          </a-descriptions-item>
        </a-descriptions>
        <h3>Company</h3>
        <a-descriptions :column="1" size="small" class="col-fixed p-0">
          <a-descriptions-item label="Type">
            {{ associate.payment_info.type }}
          </a-descriptions-item>
          <template v-if="associate.payment_info.legal_entity">
            <a-descriptions-item label="Subtype">
              {{ AGENCY_TYPES[associate.payment_info.legal_entity.type] }}
            </a-descriptions-item>
            <a-descriptions-item label="Legal Entity">
              <div class="flex gap-2">
                {{ associate.payment_info.legal_entity.name }}
              </div>
            </a-descriptions-item>
          </template>

          <a-descriptions-item label="Status">
            {{ getPaymentStatus(associate.payment_info.status) }}
          </a-descriptions-item>
          <a-descriptions-item label="Method">
            {{ getPaymentMethod(associate.payment_info.method) }}
          </a-descriptions-item>
          <a-descriptions-item label="Hourly Rate, $">
            {{ associate.payment_info.hourly_rate / 100 }}
          </a-descriptions-item>
          <a-descriptions-item v-if="associate.payment_info.method === 'salary'" label="Salary for Pay Period, $">
            {{ associate.payment_info.salary ? associate.payment_info.salary / 100 : ' - ' }}
          </a-descriptions-item>
          <a-descriptions-item label="Instant Payment">
            {{ associate.allow_instant_payment ? 'Allow' : 'Forbid' }}
          </a-descriptions-item>
          <a-descriptions-item label="Position">
            {{ associate?.role?.name }}
          </a-descriptions-item>
          <a-descriptions-item label="Schedule Type">
            {{ getScheduledTypeName(associate.schedule_type) }}
          </a-descriptions-item>
        </a-descriptions>
      </div>
      <div class="col" v-if="associate.payment_info.legal_entity">
        <div class="flex flex-column gap-2">
          <h3 class="mt-0">Legal Entity</h3>
          <a-descriptions :column="1" size="small" class="col-fixed p-0">
            <a-descriptions-item label="Name">
              {{ associate.payment_info.legal_entity.name || ' - ' }}
            </a-descriptions-item>
            <a-descriptions-item label="Tax ID">
              {{ associate.payment_info.legal_entity.tax_id || ' - ' }}
            </a-descriptions-item>
            <a-descriptions-item label="Type">
              {{ AGENCY_TYPES[associate.payment_info.legal_entity.type] || ' - ' }}
            </a-descriptions-item>
            <a-descriptions-item label="Address">
              {{
                [
                  associate.payment_info.legal_entity.address?.address_line1,
                  associate.payment_info.legal_entity.address?.address_line2,
                  associate.payment_info.legal_entity.address?.city,
                  findStateByCode(associate.payment_info.legal_entity.address?.state),
                  findCountryByCode(associate.payment_info.legal_entity.address?.country),
                  associate.payment_info.legal_entity.address?.zipcode
                ]
                  .filter(Boolean)
                  .join(', ') || ' - '
              }}
            </a-descriptions-item>
            <a-descriptions-item
              v-if="
                associate.payment_info.legal_entity.type === 'agency' ||
                associate.payment_info.legal_entity.type === 'marketplace'
              "
              label="Tax ID Letter from the IRS"
            >
              <legal-entity-attachments
                :with-title="false"
                :is-view="true"
                :files="associate.payment_info.legal_entity.tax_id_files || []"
              />
            </a-descriptions-item>
            <a-descriptions-item
              v-if="
                associate.payment_info.legal_entity.type === 'agency' ||
                associate.payment_info.legal_entity.type === 'marketplace'
              "
              label="Articles of Organization"
            >
              <legal-entity-attachments
                :with-title="false"
                :is-view="true"
                :files="associate.payment_info.legal_entity.article_files || []"
              />
            </a-descriptions-item>
            <a-descriptions-item
              v-if="
                associate.payment_info.legal_entity.type === 'independent_contractor' ||
                associate.payment_info.legal_entity.type === 'agency' ||
                associate.payment_info.legal_entity.type === 'marketplace'
              "
              label="Temporary Service Agreement (Contract)"
            >
              <legal-entity-attachments
                :with-title="false"
                :is-view="true"
                :files="associate.payment_info.legal_entity.contract_files || []"
              />
            </a-descriptions-item>
            <a-descriptions-item
              v-if="
                associate.payment_info.legal_entity.type === 'independent_contractor' ||
                associate.payment_info.legal_entity.type === 'agency' ||
                associate.payment_info.legal_entity.type === 'marketplace'
              "
              label="W9 Form"
            >
              <legal-entity-attachments
                :with-title="false"
                :is-view="true"
                :files="associate.payment_info.legal_entity.w9_files || []"
              />
            </a-descriptions-item>
          </a-descriptions>
        </div>
      </div>
    </div>

    <template slot="footer">
      <div class="flex justify-content-between">
        <a-button @click="closeModal"> Cancel </a-button>
        <div class="flex gap-2">
          <a-button icon="close" type="danger" :disabled="loadingAccept" @click="visibleConfirmModal = true">
            Reject
          </a-button>
          <a-button icon="check" type="primary" @click="openInviteFlow" :loading="loadingAccept">
            Accept
          </a-button>
          <a-button type="primary" icon="edit" @click="visibleEditModal = true">Edit Profile</a-button>
        </div>
      </div>
    </template></a-modal
  >
</template>

<script>
import UserAvatar from '@/components/ui/user-avatar.vue'
import constData from '@/helpers/const-data'
import ProfileAssociateClone from '@/modules/profile/components/profile-associate-clone.vue'
import ProfileAssociateEdit from '@/modules/profile/components/profile-associate-edit.vue'
import ShiftFormAttachments from '@/components/form-attachments.vue'
import InviteUser from '@/modules/user/components/invite-user.vue'
import { AGENCY_TYPES } from '@/store/modules/legal-entity/constants'
import { PAYMENT_METHOD, PAYMENT_WORK_STATUS } from '@/store/modules/payment/constants'
import { SCHEDULED_TYPE } from '@/store/modules/profile/constants'
import { PERMISSIONS } from '@/store/modules/roles/constants'
import { AUTH_STATUSES } from '@/store/modules/shifts/constants'
import { Alert, Button, Descriptions, Input, notification, Spin } from 'ant-design-vue'
import moment from 'moment-timezone'
import { createNamespacedHelpers } from 'vuex'
import { getTaxIdentifierLabel } from '@/utils/tax-identifier'
import { getAssociateProfileCompletionErrorMessage } from '@/modules/profile/utils/associate-profile-completion'

const { mapActions: profileActions, mapState: profileState } = createNamespacedHelpers('profile')
const { mapActions: projectActions } = createNamespacedHelpers('project')

export default {
  components: {
    'a-spin': Spin,
    'a-button': Button,
    'a-alert': Alert,
    'a-input': Input,
    'a-descriptions': Descriptions,
    'a-descriptions-item': Descriptions.Item,
    'user-avatar': UserAvatar,
    'invite-user': InviteUser,
    'legal-entity-attachments': ShiftFormAttachments,
    'clone-associate': ProfileAssociateClone,
    'profile-edit': ProfileAssociateEdit
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    alert: {
      type: Object,
      default: () => {}
    }
  },
  data() {
    return {
      loading: false,
      loadingReject: false,
      loadingAccept: false,

      countriesList: constData.countries,
      statesList: constData.usStates,

      visibleEditModal: false,
      visibleCloneModal: false,
      visibleConfirmModal: false,
      visibleInviteModal: false,
      continueInviteAfterEdit: false,

      reason: '',

      AGENCY_TYPES
    }
  },
  watch: {
    visible: {
      async handler(newVal) {
        if (newVal) await this.fetchAssociate()
      },
      immediate: true
    },
    visibleEditModal(newVal) {
      if (!newVal) {
        this.continueInviteAfterEdit = false
      }
    }
  },
  computed: {
    ...profileState({
      associate: state => state.associate
    }),

    associatePermissions() {
      if (!this.associate) return '-'

      return !this.associate.permissions.length
        ? 'No Permissions'
        : this.associate.permissions
            .map(item => PERMISSIONS[item.name]?.title)
            .filter(Boolean)
            .join(', ')
    },

    associateAuthorizations() {
      if (!this.associate) return '-'

      return !this.associate.available_auth_types.length
        ? 'No Available Approvals'
        : this.associate.available_auth_types
            .map(item => AUTH_STATUSES[item]?.title)
            .filter(Boolean)
            .join(', ')
    }
  },
  methods: {
    ...profileActions(['getAssociate', 'setAssociate']),
    ...projectActions(['acceptJoinRequest', 'rejectJoinRequest']),

    closeModal() {
      this.$emit('update:visible', false)
    },
    closeConfirmModal() {
      this.reason = ''
      this.visibleConfirmModal = false
    },

    showNotification(type, message, description) {
      notification[type]({
        message,
        description
      })
    },

    openInviteFlow() {
      const profileCompletionError = getAssociateProfileCompletionErrorMessage(this.associate)

      if (profileCompletionError) {
        this.continueInviteAfterEdit = true
        this.showNotification('warning', 'Validation Error', profileCompletionError)
        this.visibleEditModal = true

        return
      }

      this.visibleInviteModal = true
    },

    afterSendLetter() {
      this.closeModal()
      this.$emit('save')
    },

    endEditAssociate(newAssociateNumber) {
      const newAlert = {
        ...this.alert,
        data: { ...this.alert.data, associate_number: newAssociateNumber }
      }
      this.$emit('change', newAlert)
    },

    handleProfileSaved() {
      if (!this.continueInviteAfterEdit) {
        return
      }

      this.continueInviteAfterEdit = false
      this.visibleInviteModal = true
    },

    endCloneAssociate(newAssociateNumber) {
      this.visibleCloneModal = false
      const newAlert = {
        ...this.alert,
        data: { ...this.alert.data, associate_number: newAssociateNumber }
      }

      this.$emit('change', newAlert)
    },

    getPaymentMethod(method) {
      return PAYMENT_METHOD[method]
    },
    getPaymentStatus(status) {
      return PAYMENT_WORK_STATUS[status]
    },
    getScheduledTypeName(type) {
      return SCHEDULED_TYPE[type]
    },

    formatDateString(dateString) {
      return moment(dateString).utc().format('MM/DD/YYYY')
    },

    getTaxIdentifierLabel,

    findStateByCode(code) {
      return this.statesList.find(item => item.code.toLowerCase() === code?.toLowerCase())?.name
    },

    findCountryByCode(code) {
      return this.countriesList.find(item => item.code.toLowerCase() === code?.toLowerCase())?.name
    },

    async fetchAssociate() {
      this.loading = true

      const payload = {
        associate_number: this.alert.data.associate_number,
        project_id: this.$store.state.applicationState.currentProject.id
      }

      try {
        await this.getAssociate(payload)
      } catch (error) {
        this.showNotification('error', 'Error', typeof error === 'string' ? error : error?.message)
      } finally {
        this.loading = false
      }
    },

    async reject() {
      this.loadingReject = true

      const payload = {
        reason: this.reason,
        join_project_request_id: this.alert.data.join_project_request_id
      }

      try {
        await this.rejectJoinRequest(payload)

        this.showNotification('success', 'Success', 'Join Request has been successfully rejected')

        this.$emit('save')
        this.closeModal()
      } catch (error) {
        this.showNotification('error', 'Error', typeof error === 'string' ? error : error?.message)
      } finally {
        this.loadingReject = false
      }
    }
  }
}
</script>

<style scoped>
::v-deep .ant-modal-body {
  padding-top: 0;
  min-height: 200px;
  max-height: 600px;
  overflow-y: scroll;
}

h3 {
  margin: 0;
}
</style>
